import React, { useEffect, useState } from 'react';
import { Button, Drawer, Input, InputNumber, Select } from 'antd';
import { STATUS_LABELS } from './productStatus';
import { SheetFooter, SheetBody, FieldLabel, Row, ErrorText } from './BottomSheet.style';

export const FILTER_STATUSES = ['AVAILABLE', 'RESERVED', 'DRAFT', 'SOLD'];

interface ProductFilterSheetProps {
  open: boolean;
  filters: Record<string, string>;
  onClose: () => void;
  onApply: (filters: Record<string, string>) => void;
}

const TEXT_FIELDS: { key: string; label: string; placeholder: string }[] = [
  { key: 'brand', label: 'Marke', placeholder: 'z.B. Rolex' },
  { key: 'model', label: 'Modell', placeholder: 'z.B. Submariner Date' },
  { key: 'sku', label: 'SKU', placeholder: 'z.B. MR-0001' },
  { key: 'referenceNumber', label: 'Referenznummer', placeholder: 'z.B. 126610LN' },
];

const toEuro = (cents?: string) => (cents ? Number(cents) / 100 : null);

const ProductFilterSheet: React.FC<ProductFilterSheetProps> = ({ open, filters, onClose, onApply }) => {
  const [draft, setDraft] = useState<Record<string, string>>(filters);
  const [priceMin, setPriceMin] = useState<number | null>(null);
  const [priceMax, setPriceMax] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      setDraft(filters);
      setPriceMin(toEuro(filters.priceMin));
      setPriceMax(toEuro(filters.priceMax));
    }
  }, [open, filters]);

  const set = (key: string, value?: string | null) => setDraft((d) => {
    const next = { ...d };
    if (value) next[key] = value; else delete next[key];
    return next;
  });

  const priceInvalid = priceMin !== null && priceMax !== null && priceMin > priceMax;

  const apply = () => {
    const next = { ...draft };
    delete next.priceMin;
    delete next.priceMax;
    if (priceMin !== null) next.priceMin = String(Math.round(priceMin * 100));
    if (priceMax !== null) next.priceMax = String(Math.round(priceMax * 100));
    onApply(next);
  };

  return (
    <Drawer
      placement="bottom"
      open={open}
      onClose={onClose}
      title="Filter"
      size="auto"
      styles={{ wrapper: { maxHeight: '85vh', borderRadius: '16px 16px 0 0', overflow: 'hidden' } }}
      footer={(
        <SheetFooter>
          <Button type="primary" block onClick={apply} disabled={priceInvalid} data-testid="mobile-filter-apply">Filter anwenden</Button>
          <Button type="link" block onClick={() => onApply(filters.search ? { search: filters.search } : {})}>Alle Filter zurücksetzen</Button>
        </SheetFooter>
      )}
    >
      <SheetBody>
        {TEXT_FIELDS.map(({ key, label, placeholder }) => (
          <div key={key}>
            <FieldLabel>{label}</FieldLabel>
            <Input
              value={draft[key] || ''}
              placeholder={placeholder}
              allowClear
              onChange={(e) => set(key, e.target.value.trim() ? e.target.value : null)}
              onPressEnter={() => !priceInvalid && apply()}
              data-testid={`mobile-filter-${key}`}
            />
          </div>
        ))}
        <div>
          <FieldLabel>Status</FieldLabel>
          <Select
            value={draft.status || undefined}
            placeholder="Alle"
            allowClear
            style={{ width: '100%' }}
            options={FILTER_STATUSES.map((value) => ({ value, label: STATUS_LABELS[value] }))}
            onChange={(value) => set('status', value)}
            data-testid="mobile-filter-status"
          />
        </div>
        <div>
          <FieldLabel>Preis</FieldLabel>
          <Row>
            <InputNumber value={priceMin} min={0} placeholder="Von €" inputMode="decimal" onChange={(v) => setPriceMin(v)} data-testid="mobile-filter-price-min" />
            <InputNumber value={priceMax} min={0} placeholder="Bis €" inputMode="decimal" onChange={(v) => setPriceMax(v)} data-testid="mobile-filter-price-max" />
          </Row>
          {priceInvalid && <ErrorText>„Von“ darf nicht größer als „Bis“ sein.</ErrorText>}
        </div>
      </SheetBody>
    </Drawer>
  );
};

export default ProductFilterSheet;
